import { Workflow, Users, TrendingUp, Target, MessageSquareQuote, Route, Crown } from "lucide-react";
import SEO from "@/components/SEO";

const pillars = [
  { n:"01", icon: Workflow, title:"Infrastruttura & Processi",
    text:"Standardizziamo l'infrastruttura e la mappatura dei processi chiave. Ogni flusso viene reso ripetibile, misurabile, indipendente dalla memoria delle persone." },
  { n:"02", icon: Users, title:"Team & Cultura Operativa",
    text:"Ottimizziamo la gestione e il clima del team. Trasformiamo i collaboratori da meri esecutori a risorse autonome e propositive." },
  { n:"03", icon: TrendingUp, title:"Scalabilità & Marginalità",
    text:"Analizziamo la scalabilità, la marginalità reale di prodotti e servizi e la pianificazione a 12 mesi, riducendo la dipendenza dell'azienda dal solo tempo del titolare." },
  { n:"04", icon: Target, title:"Brand Core & USP",
    text:"Consolidiamo il cuore del Brand e la sua Unique Selling Proposition. Rendiamo il posizionamento inequivocabile, prima ancora della comunicazione." },
  { n:"05", icon: MessageSquareQuote, title:"Storytelling & Tono di Voce",
    text:"Eliminiamo l'improvvisazione comunicativa. Progettiamo uno storytelling costante e un tono di voce coerente tra canali online e offline, traducendo il valore reale in materiale di vendita d'eccellenza." },
  { n:"06", icon: Route, title:"Customer Journey & Retention",
    text:"Ingegnerizziamo il viaggio del cliente, dall'onboarding fluido al supporto post-vendita. Creiamo strategie attive per stimolare il riacquisto e trasformare i clienti in promotori del brand." },
  { n:"07", icon: Crown, title:"Indipendenza del Titolare",
    text:"Costruiamo la vera indipendenza del titolare: l'azienda deve poter crescere anche senza la sua presenza fisica quotidiana. Delega, sistemi, controllo strategico." },
];

export default function Metodo() {
  return (
    <>
      <SEO
        path="/metodo"
        title="Il Metodo A.L.L.I.N.E.A. — 7 Pilastri per Aziende e PMI"
        description="Il Metodo A.L.L.I.N.E.A.© è il protocollo proprietario di Roberta Castagna che unisce brand strategy e ottimizzazione dei processi aziendali in 7 pilastri: Infrastruttura, Team, Scalabilità, Brand, Storytelling, Customer Journey, Indipendenza del Titolare."
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "HowTo",
          "name": "Metodo A.L.L.I.N.E.A. — I 7 Pilastri dell'Assetto Aziendale",
          "description": "Protocollo proprietario di reingegnerizzazione delle aziende e delle PMI in 7 pilastri.",
          "step": [
            {"@type":"HowToStep","name":"Infrastruttura & Processi","text":"Standardizzazione dei flussi chiave."},
            {"@type":"HowToStep","name":"Team & Cultura Operativa","text":"Autonomia e proattività del team."},
            {"@type":"HowToStep","name":"Scalabilità & Marginalità","text":"Analisi della marginalità reale e pianificazione a 12 mesi."},
            {"@type":"HowToStep","name":"Brand Core & USP","text":"Consolidamento del posizionamento e USP."},
            {"@type":"HowToStep","name":"Storytelling & Tono di Voce","text":"Coerenza comunicativa on/offline."},
            {"@type":"HowToStep","name":"Customer Journey & Retention","text":"Ingegneria del viaggio del cliente."},
            {"@type":"HowToStep","name":"Indipendenza del Titolare","text":"L'azienda cresce anche senza la presenza fisica del fondatore."}
          ]
        }}
      />
      {/* HERO */}
      <section className="section-pad relative overflow-hidden">
        <img
          src="https://customer-assets-v7afamib.emergentagent.net/job_process-gap/artifacts/f9hrwe9q_STORIES%20CHALLENGE%20FEBBRAIO%20%285%29.png"
          alt=""
          aria-hidden="true"
          className="absolute -right-16 top-1/2 -translate-y-1/2 w-[380px] max-w-none opacity-[0.07] pointer-events-none select-none hidden md:block"
        />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 relative">
          <div className="max-w-4xl" data-reveal>
            <p className="eyebrow mb-4">Il Metodo A.L.L.I.N.E.A.<sup className="text-[10px]">©</sup></p>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.05] text-[#2A3229]">
              L'efficacia non è un'intuizione. <span className="text-[#323C36]">È un assetto.</span>
            </h1>
            <div className="mt-10 space-y-6 text-lg text-[#5A6058] leading-relaxed max-w-3xl">
              <p>Molte piccole e medie imprese non falliscono per mancanza di visione, ma per un problema di attrito interno: la strategia non parla con l'operatività, il posizionamento di marca non si riflette nei processi, e la crescita dipende esclusivamente dalla presenza fisica del titolare.</p>
              <p className="text-[#2A3229]">Il <strong className="text-[#323C36]">Metodo A.L.L.I.N.E.A.</strong> è il protocollo ingegneristico di Brand Strategy e Ottimizzazione Processi progettato per eliminare il caos aziendale, strutturare la crescita e trasformare l'impresa in un meccanismo autonomo, scalabile e profittevole.</p>
            </div>
          </div>
        </div>
      </section>

      {/* MANIFESTO */}
      <section className="bg-[#EDE7DD] section-pad">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <h2 className="font-display text-4xl sm:text-5xl text-[#2A3229] leading-tight">
              Perché l'approccio tradizionale sta <span className="text-[#A8674F]">frammentando</span> la tua azienda.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-6 text-[#2A3229] text-lg leading-relaxed">
            <p>Di solito, quando un'azienda ha un problema, si muove a compartimenti stagni: ingaggia un'agenzia per il marketing, un consulente per la gestione interna e un coach per la leadership.</p>
            <p className="text-[#5A6058]">Il risultato? Risorse disperse, messaggi contraddittori e zero controllo sui dati.</p>
            <p><strong className="text-[#323C36]">A.L.L.I.N.E.A. nasce per unire ciò che il mercato tende a separare</strong>: la visione strategica del Brand e l'efficienza millimetrica dei Processi aziendali. Se un solo elemento è fuori asse, l'intera struttura rallenta.</p>
          </div>
        </div>
      </section>

      {/* ARCHITETTURA — 7 PILLARS */}
      <section className="section-pad">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mb-12" data-reveal>
            <p className="eyebrow mb-4">L'Architettura del Metodo</p>
            <h2 className="font-display text-4xl sm:text-5xl leading-tight text-[#2A3229]">
              I <span className="text-[#323C36]">7 Pilastri</span> dell'Assetto Aziendale
            </h2>
            <p className="mt-6 text-lg text-[#5A6058] leading-relaxed">
              Il metodo mappa l'intera anatomia della tua impresa attraverso una diagnosi aziendale. Non analizziamo reparti isolati, ma le relazioni che li collegano, intervenendo su 7 aree critiche.
            </p>
          </div>

          <div className="max-w-6xl mx-auto">
            {pillars.map((p, i) => {
              const Icon = p.icon;
              return (
              <article
                key={i}
                data-testid={`pillar-${i}`}
                data-reveal
                data-reveal-delay={`${(i % 4) + 1}`}
                className="group relative border-t border-[#323C36]/15 py-7 sm:py-9 grid grid-cols-12 gap-6 sm:gap-10 items-start transition-colors duration-500 hover:bg-[#EDE7DD]/60 -mx-4 px-4 sm:-mx-6 sm:px-6"
              >
                {/* Numero grande */}
                <div className="col-span-2 sm:col-span-2">
                  <span className="font-display block leading-none text-[#A8674F] font-light text-3xl sm:text-4xl lg:text-5xl transition-all duration-500 group-hover:text-[#323C36] group-hover:-translate-y-1">
                    {p.n}
                  </span>
                </div>

                {/* Testo pilastro */}
                <div className="col-span-10 sm:col-span-8">
                  <h3 className="font-display text-xl sm:text-2xl text-[#2A3229] leading-tight tracking-tight mb-2 sm:mb-3 font-normal transition-transform duration-500 group-hover:translate-x-1">
                    {p.title}
                  </h3>
                  <p className="text-sm sm:text-base text-[#5A6058] leading-[1.7] max-w-3xl">
                    {p.text}
                  </p>
                </div>

                {/* Icona */}
                <div className="hidden sm:flex col-span-2 justify-end pt-1">
                  <span className="inline-flex items-center justify-center w-11 h-11 lg:w-12 lg:h-12 rounded-full border border-[#323C36]/15 text-[#A8674F] transition-all duration-500 group-hover:border-[#323C36] group-hover:bg-[#323C36] group-hover:text-[#FAC075] group-hover:rotate-6">
                    <Icon size={18} strokeWidth={1.5}/>
                  </span>
                </div>
              </article>
              );
            })}
            {/* Linea di chiusura */}
            <div className="border-t border-[#323C36]/15"/>
          </div>
        </div>
      </section>

      {/* LEAD MAGNET CTA */}
      <section className="bg-[#2A3229] text-[#F6F3EF] section-pad">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 text-center" data-reveal>
          <p className="eyebrow !text-[#FAC075] mb-4">Check‑Up di Assetto Aziendale</p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-tight">
            La tua azienda è davvero <span className="text-[#FAC075]">Allineata</span>? Scoprilo in 10 minuti.
          </h2>
          <div className="mt-10 max-w-3xl mx-auto space-y-6 text-lg text-[#F6F3EF]/80 leading-relaxed text-left">
            <p>Non rimandare il controllo della tua struttura. Ho sintetizzato le metriche chiave del Metodo A.L.L.I.N.E.A. in uno strumento di autovalutazione strategica avanzato.</p>
            <p>Accedi al <strong className="text-[#F6F3EF]">Check‑Up di Assetto Aziendale</strong>: riceverai un report preliminare entro 24h lavorative che evidenzia l'area critica su cui devi intervenire oggi stesso per sbloccare la tua crescita.</p>
          </div>
          <div className="mt-12">
            <a data-testid="metodo-cta-quiz" href="/checkup" className="btn-accent">Fai il Check‑Up di Assetto Aziendale →</a>
          </div>
        </div>
      </section>

      {/* LEGAL FOOTER NOTE */}
      <section className="bg-[#EDE7DD] py-10">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <p className="text-xs text-[#5A6058] leading-relaxed">
            Il <strong className="text-[#323C36]">Metodo A.L.L.I.N.E.A.<sup>©</sup></strong> è una metodologia proprietaria di Roberta Castagna, tutelata ai sensi della normativa sulla proprietà intellettuale (Legge 633/1941). Ogni tentativo di plagio, parziale o totale, dei flussi di audit e delle strutture concettuali verrà perseguito a termini di legge.
          </p>
        </div>
      </section>
    </>
  );
}
